import { MapLayerCategory } from '../../../utils/layer-registry';
import {
  HIKE_LIST_OVERLAY_SOURCE_ID,
  HIKE_LIST_TILE_MAX_ZOOM,
  HIKE_LIST_TILE_MIN_ZOOM,
  HikeListLayerStyle,
} from '../../../utils/hikeListOverlay';
import { PERSONAL_TRAIL_LINE_Z } from './LineSourceHelper';

// Draws the "Hike lists" overlay from the get_user_hike_list_hikes vector-tile
// source (Martin, via the Rails /vector proxy) — like the other personal-trail
// overlays, except every tile feature carries its list's id, so colour, width,
// opacity, which lists show and which list sits on top come from
// `hikeListLayerStyle` and are re-applied in place when they change.
//
// Layer ids follow LineSourceHelper's convention — the main line's id is the
// source id — so the map click handler can treat it like the other sources.

const MAIN_ID = HIKE_LIST_OVERLAY_SOURCE_ID;
const BORDER_ID = `${HIKE_LIST_OVERLAY_SOURCE_ID}-border`;
const NAMES_ID = `${HIKE_LIST_OVERLAY_SOURCE_ID}-names`;
const SOURCE_LAYER = 'data';

export class HikeListOverlayLayer {
  /** True once the overlay's layers are on the map. */
  static isShown(): boolean {
    const map: any = window.map;
    return !!map?.getLayer?.(MAIN_ID);
  }

  /**
   * Adds the overlay with `tileUrl`, or — when it is already on the map — just
   * restyles it. `reloadTiles` swaps in `tileUrl` (a new cache-buster) so lists
   * that gained or lost hikes redraw.
   */
  static show(style: HikeListLayerStyle, tileUrl: string, { reloadTiles = false } = {}) {
    const map: any = window.map;
    if (!map || !window.layerRegistry) return;

    if (this.isShown()) {
      if (reloadTiles) map.getSource(HIKE_LIST_OVERLAY_SOURCE_ID)?.setTiles?.([tileUrl]);
      this.restyle(style);
      return;
    }

    window.layerRegistry.addLayer(
      {
        id: MAIN_ID,
        type: 'line',
        source: HIKE_LIST_OVERLAY_SOURCE_ID,
        'source-layer': SOURCE_LAYER,
        filter: style.filter,
        layout: { 'line-join': 'round', 'line-cap': 'round', 'line-sort-key': style.lineSortKey },
        paint: {
          'line-color': style.lineColor,
          'line-width': style.lineWidth,
          'line-opacity': style.lineOpacity,
        },
      } as any,
      MapLayerCategory.Annotations,
      {
        // Above every other personal-trail line, completed included.
        z: PERSONAL_TRAIL_LINE_Z.hikeLists,
        // @ts-ignore — the registry adds the source with the first layer
        source: {
          id: HIKE_LIST_OVERLAY_SOURCE_ID,
          spec: {
            type: 'vector',
            tiles: [tileUrl],
            minzoom: HIKE_LIST_TILE_MIN_ZOOM,
            maxzoom: HIKE_LIST_TILE_MAX_ZOOM,
          },
        },
      },
    );

    window.layerRegistry.addLayer(
      {
        id: BORDER_ID,
        type: 'line',
        source: HIKE_LIST_OVERLAY_SOURCE_ID,
        'source-layer': SOURCE_LAYER,
        filter: style.filter,
        layout: { 'line-join': 'round', 'line-cap': 'round', 'line-sort-key': style.lineSortKey },
        paint: {
          'line-color': style.haloColor,
          'line-width': style.haloWidth,
          'line-opacity': style.lineOpacity,
        },
      } as any,
      MapLayerCategory.Annotations,
      { z: 0 },
    );

    // "<list> – <hike>" along the line (the tile's `title`), styled like the other trail names.
    window.layerRegistry.addLayer(
      {
        id: NAMES_ID,
        type: 'symbol',
        source: HIKE_LIST_OVERLAY_SOURCE_ID,
        'source-layer': SOURCE_LAYER,
        filter: style.filter,
        minzoom: 9,
        layout: {
          'symbol-placement': 'line',
          'symbol-sort-key': style.symbolSortKey,
          'text-field': ['get', 'title'],
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-size': 12,
          'text-allow-overlap': false,
          'symbol-spacing': 250,
          'text-max-angle': 30,
          'text-pitch-alignment': 'map',
        },
        paint: {
          'text-color': '#333333',
          'text-halo-color': 'rgba(255, 255, 255, 0.8)',
          'text-halo-width': 1.5,
        },
      } as any,
      MapLayerCategory.Annotations,
      { z: 2 },
    );
  }

  /** Re-applies colours, widths, opacity, stacking and which lists show — no new tiles. */
  static restyle(style: HikeListLayerStyle) {
    const map: any = window.map;
    if (!map?.getLayer) return;
    if (map.getLayer(MAIN_ID)) {
      map.setFilter(MAIN_ID, style.filter);
      map.setLayoutProperty(MAIN_ID, 'line-sort-key', style.lineSortKey);
      map.setPaintProperty(MAIN_ID, 'line-color', style.lineColor);
      map.setPaintProperty(MAIN_ID, 'line-width', style.lineWidth);
      map.setPaintProperty(MAIN_ID, 'line-opacity', style.lineOpacity);
    }
    if (map.getLayer(BORDER_ID)) {
      map.setFilter(BORDER_ID, style.filter);
      map.setLayoutProperty(BORDER_ID, 'line-sort-key', style.lineSortKey);
      map.setPaintProperty(BORDER_ID, 'line-color', style.haloColor);
      map.setPaintProperty(BORDER_ID, 'line-width', style.haloWidth);
      map.setPaintProperty(BORDER_ID, 'line-opacity', style.lineOpacity);
    }
    if (map.getLayer(NAMES_ID)) {
      map.setFilter(NAMES_ID, style.filter);
      map.setLayoutProperty(NAMES_ID, 'symbol-sort-key', style.symbolSortKey);
    }
  }

  static hide() {
    if (!window.layerRegistry) return;
    window.layerRegistry.removeLayer(NAMES_ID);
    window.layerRegistry.removeLayer(BORDER_ID);
    window.layerRegistry.removeLayer(MAIN_ID);
  }
}
